import { Link } from "@tanstack/react-router";
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { bytes, eta } from "../lib/format";
import { type Upload, uploads } from "../lib/uploads";

export const useUploads = () => useSyncExternalStore(uploads.subscribe, uploads.snapshot);

const LABEL: Record<Upload["status"], string> = {
  queued: "Waiting",
  starting: "Starting",
  uploading: "Uploading",
  waiting: "Connection lost, retrying",
  done: "Uploaded, now processing",
  failed: "Failed",
  cancelled: "Cancelled",
  interrupted: "Paused",
  duplicate: "Already in the library?",
};

/** Bottom-right tray: every upload in progress, visible on every Studio screen. */
export function UploadTray() {
  const list = useUploads();
  const [collapsed, setCollapsed] = useState(false);
  if (!list.length) return null;
  const active = list.filter((u) => ["queued", "starting", "uploading", "waiting"].includes(u.status));
  const total = active.reduce((n, u) => n + u.size, 0),
    sent = active.reduce((n, u) => n + u.sent, 0);
  const speed = active.reduce((n, u) => n + u.speed, 0);
  const title = active.length
    ? `Uploading ${active.length} file${active.length > 1 ? "s" : ""} · ${Math.floor((sent / total) * 100)}%`
    : list.some((u) => u.status === "duplicate")
      ? "Check before uploading"
      : list.some((u) => u.status === "interrupted")
        ? "Uploads paused"
        : "Uploads";

  return (
    <section className="tray" aria-label="Uploads">
      <header>
        <button type="button" className="tray-title" onClick={() => setCollapsed((c) => !c)} aria-expanded={!collapsed}>
          <span>{title}</span>
          {active.length > 0 && speed > 0 && <small className="muted">{eta((total - sent) / speed)}</small>}
        </button>
        {!active.length && (
          <button type="button" className="btn ghost sm" onClick={() => uploads.clearFinished()}>
            Clear
          </button>
        )}
      </header>
      {!collapsed && (
        <ul className="tray-items">
          {list.map((u) => (
            <TrayItem key={u.key} u={u} />
          ))}
        </ul>
      )}
    </section>
  );
}

function TrayItem({ u }: { u: Upload }) {
  const pick = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const pct = Math.floor((u.sent / u.size) * 100);
  // biome-ignore lint/correctness/useExhaustiveDependencies: a new status clears the last error
  useEffect(() => setError(null), [u.status]);

  return (
    <li className={`tray-item ${u.status}`}>
      <div className="tray-row">
        <span className="ellipsis" title={u.name}>
          {u.name}
        </span>
        <span className="mono muted">{bytes(u.size)}</span>
      </div>
      {!["done", "cancelled", "duplicate"].includes(u.status) && (
        <div className="bar" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={`${u.name} upload`}>
          <i style={{ width: `${pct}%` }} />
        </div>
      )}
      <div className="tray-row small">
        <span className={u.status === "failed" ? "err" : "muted"}>
          {u.status === "duplicate" && u.duplicate ? (
            <>
              Looks like{" "}
              <Link to="/media" search={{ id: u.duplicate.id }} className="tray-dup">
                {u.duplicate.originalFilename}
              </Link>
              , already in the library.
            </>
          ) : (
            (u.error ?? LABEL[u.status])
          )}
          {u.status === "uploading" && ` · ${pct}%`}
          {u.status === "uploading" && u.speed > 0 && ` · ${bytes(u.speed)}/s`}
        </span>
        <span className="tray-actions">
          {u.status === "interrupted" && (
            <>
              <button type="button" className="link" onClick={() => pick.current?.click()}>
                Choose file to resume
              </button>
              <input
                ref={pick}
                type="file"
                hidden
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) setError(uploads.resume(u.key, f));
                  e.target.value = "";
                }}
              />
            </>
          )}
          {u.status === "failed" && (
            <button type="button" className="link" onClick={() => uploads.retry(u.key)}>
              Retry
            </button>
          )}
          {u.status === "duplicate" && (
            <button type="button" className="link" onClick={() => uploads.uploadAnyway(u.key)}>
              Upload anyway
            </button>
          )}
          {["queued", "starting", "uploading", "waiting", "interrupted", "failed"].includes(u.status) ? (
            <button type="button" className="link muted" onClick={() => void uploads.cancel(u.key)}>
              Cancel
            </button>
          ) : (
            <button
              type="button"
              className="link muted"
              onClick={() => uploads.dismiss(u.key)}
              aria-label={u.status === "duplicate" ? `Skip ${u.name}` : `Dismiss ${u.name}`}
            >
              {u.status === "duplicate" ? "Skip" : "✕"}
            </button>
          )}
        </span>
      </div>
      {error && <p className="err small">{error}</p>}
    </li>
  );
}
